/**
 * Page Jurisprudence RGPD
 * Route : /jurisprudence/rgpd
 *
 * Deux modes :
 *  - Browse  : scroll paginé de toutes les décisions is_rgpd=true
 *  - Recherche : recherche sémantique avec filtre is_rgpd automatique
 *
 * Spécificités futures prévues :
 *  - Filtres par sous_theme, base_legale, issue_personne
 *  - Vue "jurisprudence CJUE liée" par décision
 *  - Statistiques RGPD (taux de succès, articles les plus invoqués)
 */

import { useEffect, useState, useCallback, useRef } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useTranslation, Trans } from 'react-i18next';
import { getDateLocale } from '@/i18n';
import { Loader } from '@/components/Loader';
import {
  PublicHeader, PublicFooter, MCPBadge,
  type PublicFiche,
} from '@/components/jurisprudence';
import { FicheCardRGPD } from '@/components/jurisprudence/ficheCard';
import { usePublicAuth } from '@/hooks/usePublicAuth';
import { SmartSearchBar } from '@/components/jurisprudence/SmartSearch';
import { SearchModeToggle, useSearchMode, type SearchMode } from '@/components/jurisprudence/SearchModeToggle';

const SITE_URL     = 'https://smartlawyer.ai';
const OG_IMAGE     = `${SITE_URL}/api/fiches/og-image/default.png`;
const LIMIT        = 20;
const DISPLAY_STEP = 10;

// Valeurs réelles stockées dans Qdrant (champ rgpd_sous_theme, générées par le LLM)
// `label` = clé i18n (rgpd.themes.*), traduite au rendu
const SOUS_THEMES: { value: string; label: string }[] = [
  'consentement', 'droit_acces', 'effacement', 'donnees_sensibles', 'surveillance_salarie',
  'violation_donnees', 'transfert_international', 'responsable_traitement', 'cookies',
  'donnees_sante', 'cnil_sanction', 'dpo', 'loi_informatique_libertes',
].map(value => ({ value, label: `rgpd.themes.${value}` }));

export default function PublicRGPD() {
  const { t } = useTranslation();
  const locale = getDateLocale();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = usePublicAuth();

  const [mode, setMode]               = useState<'browse' | 'search'>('browse');
  const [query, setQuery]             = useState(searchParams.get('q') || '');
  const [theme, setTheme]             = useState(searchParams.get('theme') || '');
  const [loading, setLoading]         = useState(true);
  const [initialized, setInitialized] = useState(false);
  const [error, setError]             = useState<string | null>(null);

  // Browse state
  const [browseResults, setBrowseResults] = useState<PublicFiche[]>([]);
  const [browseTotal, setBrowseTotal]     = useState(0);
  const [page, setPage]                   = useState(0);
  const cursorsRef = useRef<(string | null)[]>([null]);

  // Search state
  const [allResults, setAllResults]     = useState<PublicFiche[]>([]);
  const [results, setResults]           = useState<PublicFiche[]>([]);

  // Mode recherche
  const [searchMode, setSearchMode] = useState<SearchMode>('libre');
  const toggleMode = useSearchMode(searchMode, setSearchMode, 'libre');

  // Corpus cross-counts
  const [corpusCounts, setCorpusCounts] = useState<{
    total: number; rgpd_count: number; arrets_principe: number; cjue_count: number;
    reglements_eu_count: number; reglements_eu_reglements: number;
  } | null>(null);

  useEffect(() => {
    fetch('/api/fiches/corpus-stats')
      .then(r => r.ok ? r.json() : null)
      .then(d => d && setCorpusCounts(d))
      .catch(() => null);
  }, []);
  const [displayCount, setDisplayCount] = useState(0);
  const [searchTime, setSearchTime]     = useState<number | null>(null);

  // ── Browse ──────────────────────────────────────────────────────────────────
  const fetchBrowse = useCallback(async (pageNum: number, th: string) => {
    setLoading(true);
    setError(null);
    const cursor = pageNum === 0 ? null : (cursorsRef.current[pageNum] ?? null);
    const params = new URLSearchParams({ limit: String(LIMIT) });
    if (cursor) params.set('cursor', cursor);
    if (th)     params.set('sous_theme', th);

    try {
      const res  = await fetch(`/api/rgpd/browse?${params}`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setBrowseResults(data.results || []);
      setBrowseTotal(data.total || 0);
      if (data.next_cursor != null) cursorsRef.current[pageNum + 1] = data.next_cursor;
    } catch {
      setError(t('rgpd.errors.load'));
    } finally {
      setLoading(false);
      setInitialized(true);
    }
  }, []);

  // ── Recherche sémantique ────────────────────────────────────────────────────
  const doSearch = useCallback(async (q: string, th: string) => {
    if (!q.trim()) return;
    setLoading(true);
    setError(null);
    setAllResults([]);
    setResults([]);
    setDisplayCount(0);
    const t0 = performance.now();

    try {
      const res = await fetch('/api/rgpd/search', {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ query: q, limit: 50, sous_theme: th || undefined }),
      });
      const data    = await res.json();
      const fetched: PublicFiche[] = data.results || [];
      setAllResults(fetched);
      setResults(fetched.slice(0, DISPLAY_STEP));
      setDisplayCount(DISPLAY_STEP);
      setSearchTime(Math.round(performance.now() - t0));
    } catch {
      setError(t('rgpd.errors.search'));
    } finally {
      setLoading(false);
      setInitialized(true);
    }
  }, [user]);

  // ── Init ────────────────────────────────────────────────────────────────────
  useEffect(() => {
    const q  = searchParams.get('q') || '';
    const th = searchParams.get('theme') || '';
    setQuery(q);
    setTheme(th);
    if (q) { setMode('search'); doSearch(q, th); }
    else   { setMode('browse'); fetchBrowse(0, th); }
  }, []);

  // ── Handler recherche guidée ────────────────────────────────────────────────
  const handleGuidedSearch = useCallback((q: string, filters: Record<string, string>) => {
    const th = filters.sous_theme ?? '';
    setQuery(q);
    setTheme(th);
    setMode('search');
    doSearch(q, th);
  }, []);

  // ── Handlers ────────────────────────────────────────────────────────────────
  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (!query.trim()) {
      setMode('browse'); setPage(0); cursorsRef.current = [null];
      setSearchParams(theme ? { theme } : {});
      fetchBrowse(0, theme);
      return;
    }
    setMode('search');
    const params: Record<string, string> = { q: query };
    if (theme) params.theme = theme;
    setSearchParams(params);
    doSearch(query, theme);
  };

  const handleTheme = (th: string) => {
    setTheme(th); setPage(0); cursorsRef.current = [null];
    if (mode === 'search' && query.trim()) doSearch(query, th);
    else fetchBrowse(0, th);
    const params: Record<string, string> = {};
    if (query) params.q = query;
    if (th)    params.theme = th;
    setSearchParams(params);
  };

  const handleReset = () => {
    setQuery(''); setTheme(''); setMode('browse'); setPage(0);
    cursorsRef.current = [null];
    setSearchParams({});
    fetchBrowse(0, '');
  };

  const goToPage = (p: number) => {
    if (p > 0 && cursorsRef.current[p] === undefined) return;
    setPage(p);
    fetchBrowse(p, theme);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const loadMore = () => {
    const next = displayCount + DISPLAY_STEP;
    setResults(allResults.slice(0, next));
    setDisplayCount(next);
  };

  const totalPages    = Math.ceil(browseTotal / LIMIT);
  const hasNextCursor = cursorsRef.current[page + 1] != null;

  // ── Render ──────────────────────────────────────────────────────────────────
  return (
    <>
      <Helmet>
        <title>{t('rgpd.meta.title')}</title>
        <meta name="description" content={t('rgpd.meta.description')} />
        <link rel="canonical" href={`${SITE_URL}/jurisprudence/rgpd`} />
        <meta property="og:title"       content={t('rgpd.meta.title')} />
        <meta property="og:description" content={t('rgpd.meta.og_description')} />
        <meta property="og:image"       content={OG_IMAGE} />
        <meta property="og:url"         content={`${SITE_URL}/jurisprudence/rgpd`} />
      </Helmet>

      <div className="min-h-screen bg-background flex flex-col">
        <PublicHeader />

        <main className="flex-1 max-w-5xl mx-auto w-full px-4 sm:px-6 py-10">

          {/* Breadcrumb */}
          <nav className="text-xs text-muted-foreground mb-6 flex items-center gap-1.5">
            <Link to="/" className="hover:text-foreground transition-colors">{t('cjue.breadcrumb.home')}</Link>
            <span className="text-muted-foreground/40">/</span>
            <Link to="/browse" className="hover:text-foreground transition-colors">{t('nav.jurisprudence')}</Link>
            <span className="text-muted-foreground/40">/</span>
            <span className="text-foreground">{t('rgpd.abbr')}</span>
          </nav>

          {/* ── En-tête épuré ── */}
          <div className="mb-6">
            {/* Titre + cross-links sur la même ligne */}
            <div className="flex items-start justify-between gap-4 mb-4 flex-wrap">
              <div className="flex items-center gap-3">
                <span className="inline-flex items-center justify-center w-9 h-9 rounded-xl bg-blue-600 text-white shrink-0">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12 1L3 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5l-9-4z"/>
                  </svg>
                </span>
                <div>
                  <h1 className="text-xl sm:text-2xl font-bold leading-tight">{t('cjue.header.corpus')} <em className="not-italic text-primary">{t('rgpd.abbr')}</em></h1>
                </div>
              </div>
              <div className="flex items-center gap-3 text-[11px] text-muted-foreground flex-wrap">
                <Link to="/jurisprudence/principes" className="inline-flex items-center gap-1 hover:text-foreground transition-colors">
                  ⭐ {t('cjue.header.corpus_principles')}
                  {corpusCounts && <span className="font-mono text-[10px] opacity-60">{corpusCounts.arrets_principe.toLocaleString(locale)}</span>}
                </Link>
                <span className="text-border">·</span>
                <Link to="/jurisprudence/cjue" className="inline-flex items-center gap-1 hover:text-foreground transition-colors">
                  🇪🇺 {t('cjue.search.corpus_title')}
                  {corpusCounts && <span className="font-mono text-[10px] opacity-60">{corpusCounts.cjue_count.toLocaleString(locale)}</span>}
                </Link>
                <span className="text-border">·</span>
                <Link to="/reglements" className="inline-flex items-center gap-1 hover:text-foreground transition-colors">
                  📋 {t('cjue.header.eu_regulations')}
                  {corpusCounts && corpusCounts.reglements_eu_count > 0 && <span className="font-mono text-[10px] opacity-60">{corpusCounts.reglements_eu_count.toLocaleString(locale)}</span>}
                </Link>
              </div>
            </div>

            {/* Stats en ligne — Legal Graph */}
            <div className="flex flex-wrap gap-2">
              <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-blue-50 dark:bg-blue-900/20 border border-blue-100 dark:border-blue-800/50">
                <span className="text-base font-bold text-foreground tabular-nums">
                  {corpusCounts
                    ? (corpusCounts.rgpd_count || corpusCounts.total).toLocaleString(locale)
                    : <span className="inline-block w-10 h-4 bg-blue-100 dark:bg-blue-800/40 rounded animate-pulse" />}
                </span>
                <span className="text-[10px] text-muted-foreground uppercase tracking-wide">{t('rgpd.stats.decisions')}</span>
              </div>
              <Link to="/jurisprudence/cjue"
                className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-indigo-50 dark:bg-indigo-900/10 border border-indigo-100 dark:border-indigo-800/40 hover:border-indigo-300 transition-colors">
                <span className="text-base font-bold text-indigo-700 dark:text-indigo-400 tabular-nums">
                  {corpusCounts
                    ? corpusCounts.cjue_count.toLocaleString(locale)
                    : <span className="inline-block w-10 h-4 bg-indigo-100 rounded animate-pulse" />}
                </span>
                <span className="text-[10px] text-muted-foreground uppercase tracking-wide">{t('rgpd.stats.linked_cjue')}</span>
              </Link>
              <Link to="/reglements"
                className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-violet-50 dark:bg-violet-900/10 border border-violet-100 dark:border-violet-800/40 hover:border-violet-300 transition-colors">
                <span className="text-base font-bold text-violet-700 dark:text-violet-400 tabular-nums">
                  {corpusCounts && corpusCounts.reglements_eu_count > 0
                    ? corpusCounts.reglements_eu_count.toLocaleString(locale)
                    : <span className="inline-block w-10 h-4 bg-violet-100 rounded animate-pulse" />}
                </span>
                <span className="text-[10px] text-muted-foreground uppercase tracking-wide">{t('rgpd.stats.eu_articles')}</span>
              </Link>
              <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-muted/60 border border-border">
                <span className="text-base font-bold text-foreground">2018+</span>
                <span className="text-[10px] text-muted-foreground uppercase tracking-wide">{t('rgpd.stats.since_gdpr')}</span>
              </div>
            </div>
          </div>

          {/* ── Zone de recherche unifiée — style homepage ── */}
          <div className="rounded-2xl border border-border bg-muted/40 px-5 sm:px-6 pt-4 pb-4 mb-6">
            {/* Toprow : compteur + toggle */}
            <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
              <p className="text-[13px] font-bold text-foreground">
                <span className="text-lg leading-none">🔒</span>{' '}
                {corpusCounts
                  ? <><strong className="text-primary">{(corpusCounts.rgpd_count || corpusCounts.total).toLocaleString(locale)}</strong> {t('rgpd.search.indexed_decisions')}</>
                  : t('rgpd.search.title')}
              </p>
              <SearchModeToggle mode={searchMode} onChange={toggleMode} />
            </div>
            <div className="h-px bg-border/60 mb-3" />

            {/* Recherche */}
            {searchMode === 'guidee' ? (
              <SmartSearchBar
                context="rgpd"
                onSmartSearch={handleGuidedSearch}
                searching={loading}
              />
            ) : (
              <form onSubmit={handleSearch} className="flex gap-2">
                <div className="flex-1 flex items-center gap-3 rounded-xl border border-border bg-background px-4 py-2.5 focus-within:ring-2 focus-within:ring-primary/30 transition-all">
                  <svg className="h-4 w-4 text-muted-foreground shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                  <input
                    type="text"
                    value={query}
                    onChange={e => setQuery(e.target.value)}
                    placeholder={t('rgpd.search.placeholder')}
                    className="flex-1 bg-transparent outline-none text-[14px] placeholder:text-muted-foreground/60"
                  />
                  {query && (
                    <button type="button" onClick={handleReset} aria-label={t('cjue.search.clear')} className="text-muted-foreground hover:text-foreground transition-colors">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  )}
                </div>
                <button type="submit" disabled={loading}
                  className="px-5 py-2.5 rounded-xl bg-primary text-primary-foreground font-semibold text-[13px] hover:bg-primary/90 disabled:opacity-50 transition-colors shrink-0">
                  {loading ? <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> : t('cjue.search.submit')}
                </button>
              </form>
            )}

            {/* Filtres sous-thèmes */}
            <div className="flex flex-wrap gap-1.5 mt-3">
              <button onClick={() => handleTheme('')}
                className={`text-xs px-3 py-1 rounded-full border transition-colors ${
                  !theme ? 'bg-blue-600 text-white border-blue-600' : 'bg-background border-border text-muted-foreground hover:border-blue-400'
                }`}>
                {t('rgpd.search.all_themes')}
              </button>
              {SOUS_THEMES.map(({ value, label }) => (
                <button key={value} onClick={() => handleTheme(value)}
                  className={`text-xs px-3 py-1 rounded-full border transition-colors ${
                    theme === value ? 'bg-blue-600 text-white border-blue-600' : 'bg-background border-border text-muted-foreground hover:border-blue-400'
                  }`}>
                  {t(label)}
                </button>
              ))}
            </div>
          </div>

          {/* Compteur */}
          {initialized && !loading && (
            <div className="flex items-center justify-between mb-4">
              <p className="text-sm text-muted-foreground">
                {mode === 'browse' ? (
                  <>
                    {/* browseTotal peut être 0 avec Qdrant scroll + filtre → masquer si résultats présents */}
                    {browseTotal > 0 && (
                      <><strong className="text-foreground">{browseTotal.toLocaleString(locale)}</strong> {t('rgpd.results.decisions_suffix', { count: browseTotal })}</>
                    )}
                    {browseTotal === 0 && browseResults.length > 0 && <>{t('rgpd.results.decisions_title')}</>}
                    {browseTotal === 0 && browseResults.length === 0 && <>{t('rgpd.results.zero_decisions')}</>}
                    {theme && <> · <span className="text-foreground">{(() => { const th = SOUS_THEMES.find(st => st.value === theme); return th ? t(th.label) : theme; })()}</span></>}
                    {page > 0 && <span className="ml-2 text-muted-foreground/60">· {t('cjue.results.page_lower', { page: page + 1 })}</span>}
                  </>
                ) : (
                  <>
                    <strong className="text-foreground">{allResults.length}</strong> {t('cjue.results.results_for', { count: allResults.length })}{' '}
                    <em className="text-foreground">{t('cjue.results.quoted_query', { query })}</em>
                    {searchTime != null && (
                      <span className="ml-2 text-xs text-muted-foreground/60">
                        ({searchTime < 1000 ? `${searchTime} ms` : `${(searchTime / 1000).toFixed(2)} s`})
                      </span>
                    )}
                  </>
                )}
              </p>
              {mode === 'search' && (
                <button onClick={handleReset}
                  className="text-sm text-muted-foreground hover:text-foreground flex items-center gap-1 transition-colors">
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                  </svg>
                  {t('rgpd.results.see_all')}
                </button>
              )}
            </div>
          )}

          {/* Contenu */}
          {!initialized || (loading && !browseResults.length && !results.length) ? (
            <div className="flex flex-col items-center justify-center py-24 gap-4">
              <Loader className="!size-8" />
              <p className="text-sm text-muted-foreground">{t('common.loading')}</p>
            </div>
          ) : error ? (
            <div className="text-center py-24">
              <p className="text-5xl mb-4">⚠️</p>
              <p className="font-semibold mb-2">{error}</p>
              <button onClick={() => mode === 'browse' ? fetchBrowse(page, theme) : doSearch(query, theme)}
                className="mt-4 text-sm text-primary underline">{t('common.retry')}</button>
            </div>
          ) : mode === 'browse' ? (
            <>
              {browseResults.length === 0 ? (
                <div className="text-center py-24 text-muted-foreground">
                  <p className="text-5xl mb-4">🛡️</p>
                  <p className="font-semibold text-foreground mb-1">
                    {theme ? t('rgpd.empty.none_on_theme', { theme }) : t('rgpd.empty.none_indexed')}
                  </p>
                  <p className="text-sm">{t('rgpd.empty.enrichment_running')}</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {browseResults.map(f => <FicheCardRGPD key={f.id} fiche={f} />)}
                </div>
              )}

              {(totalPages > 1 || hasNextCursor || page > 0) && (
                <div className="flex items-center justify-center gap-2 mt-10">
                  <button disabled={page === 0 || loading} onClick={() => goToPage(page - 1)}
                    className="px-4 py-2 text-sm rounded-lg border hover:bg-accent disabled:opacity-40 transition-colors">
                    ← {t('common.previous')}
                  </button>
                  <span className="text-sm text-muted-foreground px-3">{t('cjue.results.page', { page: page + 1 })}</span>
                  <button disabled={!hasNextCursor || loading} onClick={() => goToPage(page + 1)}
                    className="px-4 py-2 text-sm rounded-lg border hover:bg-accent disabled:opacity-40 transition-colors">
                    {t('common.next')} →
                  </button>
                </div>
              )}
            </>
          ) : (
            <>
              {results.length === 0 ? (
                <div className="text-center py-24 text-muted-foreground">
                  <p className="text-5xl mb-4">🔎</p>
                  <p className="font-semibold text-foreground mb-1">{t('rgpd.empty.none_found')}</p>
                  <p className="text-sm">
                    <Trans i18nKey="rgpd.empty.try_broader" components={{ link: <Link to="/browse" className="text-primary underline" /> }} />
                  </p>
                  <button onClick={handleReset} className="mt-4 text-sm text-primary underline block mx-auto">
                    {t('rgpd.results.see_all')}
                  </button>
                </div>
              ) : (
                <div className="space-y-3">
                  {results.map(f => <FicheCardRGPD key={f.id} fiche={f} />)}
                  {displayCount < allResults.length && (
                    <div className="flex justify-center pt-4">
                      <button onClick={loadMore}
                        className="flex items-center gap-2 px-6 py-2.5 rounded-xl border-2 border-border hover:border-blue-400 hover:bg-accent text-sm font-medium transition-all">
                        {t('cjue.results.load_more', { count: allResults.length - displayCount })}
                      </button>
                    </div>
                  )}
                </div>
              )}
            </>
          )}

        </main>

        <PublicFooter />
      </div>

      <MCPBadge />
    </>
  );
}
